import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Follow-up §12 — a row-anchored menu near the bottom of the viewport used to
// render straight off-screen. `.pbnew-menu` is position:absolute with a
// hardcoded inline `top`, so a story sitting at the bottom of its status group
// opened a menu whose search and lower controls could not be reached by any
// amount of page scrolling.
//
// This measures the trigger against the viewport and returns the positioning
// style for the menu:
//   - enough room below  → drop down (unchanged behaviour);
//   - not enough below   → flip up above the trigger;
//   - not enough either way → pin to whichever side has more room and cap the
//     height so the menu scrolls internally rather than overflowing.
//
// Returns { wrapRef, menuRef, style, recalc }. Attach wrapRef to the
// position:relative wrapper and menuRef to the .pbnew-menu element.
const VIEWPORT_MARGIN = 8;
const MIN_HEIGHT = 120;

export default function useFlipMenu(open, { gap = 4, preferredHeight = 280 } = {}) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ top: 'calc(100% + 4px)' });

  const recalc = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const measured = menuRef.current?.scrollHeight || 0;
    const desired = Math.max(measured || preferredHeight, MIN_HEIGHT);

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;

    if (desired <= spaceBelow) {
      setStyle({ top: `calc(100% + ${gap}px)`, maxHeight: spaceBelow, overflowY: 'auto' });
      return;
    }
    if (desired <= spaceAbove) {
      setStyle({ bottom: `calc(100% + ${gap}px)`, maxHeight: spaceAbove, overflowY: 'auto' });
      return;
    }
    // Neither side fits: take the roomier one and let the menu scroll itself.
    if (spaceBelow >= spaceAbove) {
      setStyle({ top: `calc(100% + ${gap}px)`, maxHeight: Math.max(spaceBelow, MIN_HEIGHT), overflowY: 'auto' });
    } else {
      setStyle({ bottom: `calc(100% + ${gap}px)`, maxHeight: Math.max(spaceAbove, MIN_HEIGHT), overflowY: 'auto' });
    }
  }, [gap, preferredHeight]);

  // Measure synchronously after the menu paints, so it never shows in the wrong
  // place for a frame.
  useLayoutEffect(() => {
    if (!open) return undefined;
    recalc();
    // Re-measure while open: a resize or an ancestor scroll changes the answer.
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open, recalc]);

  return { wrapRef, menuRef, style, recalc };
}
